import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ProjectInfoModel, ProjectRelationModel, ProjectStrategicPillarModel, StrategicPillarRelatedProjectModel } from '../../../../../hub_schema/hubTypes';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable, debounceTime, distinctUntilChanged, of, switchMap, take } from 'rxjs';
import { HubRecordEditorBase } from '../../_hub_record_editor_base';
import { StrategicPillarEditService } from '../services/strategic-pillar-edit.service';
import { MatDialogRef } from '@angular/material/dialog';

@Component({
    selector: 'app-create-strategic-pillar-dialog',
    templateUrl: './create-strategic-pillar-dialog.component.html',
    styleUrls: ['./create-strategic-pillar-dialog.component.scss'],
    standalone: false
})
export class CreateStrategicPillarDialogComponent extends HubRecordEditorBase implements OnInit {
    private strategicPillarEditService: StrategicPillarEditService = inject(StrategicPillarEditService);
    private dialogRef: MatDialogRef<CreateStrategicPillarDialogComponent> = inject(MatDialogRef<CreateStrategicPillarDialogComponent>);    
    public strategicPillarForm: FormGroup;
    public relatedProjectsTypeahead: FormControl;
    public relatedProjectsSuggestions$: Observable<ProjectInfoModel[]>;
    public isSaving: boolean = false;

    public get hasRelations(): boolean {
        return this.project.relations!.some(p => p.isPrimary);
    }

    public ngOnInit(): void {
        this.strategicPillarForm = new FormGroup({
            projectStrategicPillarId: new FormControl(0),
            projectId: new FormControl(this.project.projectId),
            name: new FormControl('', Validators.maxLength(100)),
            description: new FormControl('', Validators.maxLength(500)),
            strategicPillarRelatedProjects: new FormControl([])            
        });

        this.relatedProjectsTypeahead = new FormControl('');

        this.relatedProjectsSuggestions$ = this.relatedProjectsTypeahead
            .valueChanges
            .pipe(
                debounceTime(200),
                distinctUntilChanged(),
                switchMap((term: string) => {
                    if (!this.project || !this.project.relations) {
                        return of([]);
                    }

                    let results = this.project.relations.filter((r: ProjectRelationModel) => {
                        return r.isPrimary && r.relatedProject?.name.toLowerCase().includes(term.toLowerCase());
                    }).map((r: any) => r.relatedProject).sort((a: ProjectInfoModel, b: ProjectInfoModel) => {
                        if (a.name < b.name) {
                            return -1;
                        }
                        if (a.name > b.name) {
                            return 1;
                        }
                        return 0;
                    });

                    return of(results);
                })
            );

    }

    public getRelatedProjects(): StrategicPillarRelatedProjectModel[] {
        return this.strategicPillarForm.controls.strategicPillarRelatedProjects.value;
    }

    public getTitleErrorMessage(): string {
        if (!this.strategicPillarForm.valid && this.strategicPillarForm.controls.title.invalid) {
            return 'Title cannot exceed 100 characters';
        }
        return '';
    }

    public getDescriptionErrorMessage(): string {
        if (!this.strategicPillarForm.valid && this.strategicPillarForm.controls.description.invalid) {
            return 'Description cannot exceed 500 characters';
        }
        return '';
    }

    public displayRelatedProject(option?: any): string | undefined {
        if (option && option.name) {
            return option.name;
        }
        return undefined;
    }

    public isRelatedProjectDisabled(item: ProjectInfoModel): boolean {
        return (this.strategicPillarForm.controls.strategicPillarRelatedProjects.value as StrategicPillarRelatedProjectModel[]).find(p => p.relatedProjectId === item.projectId) != null;
    }

    public relatedProjectSelected(e: MatAutocompleteSelectedEvent): void {
        const relatedProjectsControl = this.strategicPillarForm.controls.strategicPillarRelatedProjects;
        const relatedProjects = relatedProjectsControl.value as StrategicPillarRelatedProjectModel[];
        
        const project: ProjectInfoModel = e.option.value;

        const newRelatedProject: StrategicPillarRelatedProjectModel = {
            relatedProjectId: project.projectId,
            relatedProject: project
        };

        relatedProjects.push(newRelatedProject);
        relatedProjectsControl.markAsDirty();
        this.relatedProjectsTypeahead.setValue('');
    }

    public cancel(): void {
        this.dialogRef.close();
    }

    public createStrategicPillar(): void {
        this.isSaving = true;
        const pillar = this.strategicPillarForm.value;

        this.strategicPillarEditService.createProjectStrategicPillar(pillar).pipe(take(1)).subscribe({
            next: (savedPillar: ProjectStrategicPillarModel) => {
                this.isSaving = false;
                this.dialogRef.close(savedPillar);
            },
            error: (err) => {
                console.error(err);
                this.isSaving = false;
            }
        });
    }


}
